import { create } from 'zustand';
import { IndexOrderWorkData, WorkData } from '../dto/EntityRepository';

function withSequentialIndex(items: IndexOrderWorkData[]): IndexOrderWorkData[] {
  return items.map((row, i) => ({ ...row, index: i }));
}

function toIndexOrderRow(work: WorkData, index: number): IndexOrderWorkData {
  return {
    index,
    id: String(work.id),
    title: work.title,
    originUrl: work.uploadFile.originUrl,
    artist: work.artist,
  };
}

interface WorkIndexState {
  /** Works 순서 편집 UI가 열려 있는지 — 모달/오버레이는 이 플래그로 분기 */
  isIndexingMode: boolean;
  /** 인덱스 패널에서 순서/목록이 바뀌었고 아직 적용 안 함 (UNSAVED 처리용) */
  isChanged: boolean;
  /** 인덱싱 모드 on/off — off일 때 선택 목록 비움 */
  setIsIndexingMode: (enabled: boolean) => void;
  selectedIndexingWorkList: IndexOrderWorkData[];
  // isIndexingMode가 true인 상태에서 WorkInfo를 클릭하면 selectedIndexingWorkList에 추가
  addSelectedIndexingWork: (work: WorkData) => void;
  /** Indexing.tsx handleDrop 과 동일: `dropIndex` 앞 슬롯에 id 들(블록 순서는 현재 목록 순 유지) */
  mergeIndexingDropAtSlot: (draggedIds: string[], dropIndex: number) => void;
  // selectedIndexingWorkList에 있던 selectedIndexingWork를 클릭하면 selectedIndexingWorkList에서 제거
  removeSelectedIndexingWork: (id: string) => void;
  // selectedIndexingWorkList를 초기화
  clearSelectedIndexingWorkList: () => void;
  /** 서버에서 받은 수동 순서 목록으로 교체 (순번은 0..n-1로 재부여) */
  replaceSelectedIndexingWorkList: (list: IndexOrderWorkData[]) => void;
  /** CHANGE(저장) 등으로 패널 변경분을 적용 처리한 뒤 플래그만 내림 */
  clearWorkIndexingChangedFlag: () => void;
}

export const useWorkIndexStore = create<WorkIndexState>((set, get) => ({
  isIndexingMode: false,
  isChanged: false,
  setIsIndexingMode: (enabled) =>
    set(
      enabled
        ? { isIndexingMode: true, isChanged: false }
        : { isIndexingMode: false, selectedIndexingWorkList: [], isChanged: false }
    ),
  selectedIndexingWorkList: [],
  addSelectedIndexingWork: (work) => {
    if (!get().isIndexingMode) return;
    const { selectedIndexingWorkList } = get();
    if (selectedIndexingWorkList.some((row) => String(row.id) === String(work.id))) return;
    const nextIndex = selectedIndexingWorkList.length;
    set({
      selectedIndexingWorkList: [
        ...selectedIndexingWorkList,
        toIndexOrderRow(work, nextIndex),
      ],
      isChanged: true,
    });
  },
  mergeIndexingDropAtSlot: (draggedIds, dropIndex) => {
    if (!get().isIndexingMode) return;
    const orderedDataList = get().selectedIndexingWorkList;
    const idKeys = draggedIds.map(String);
    if (idKeys.length === 0) return;

    const movingSet = new Set(idKeys);
    const blockOrder = orderedDataList.filter((item) => movingSet.has(String(item.id)));
    if (blockOrder.length === 0) return;

    const without = orderedDataList.filter((item) => !movingSet.has(String(item.id)));

    let insertAt = 0;
    for (let i = 0; i < dropIndex; i++) {
      const row = orderedDataList[i];
      if (!row) break;
      if (!movingSet.has(String(row.id))) insertAt++;
    }

    const updatedList = [
      ...without.slice(0, insertAt),
      ...blockOrder,
      ...without.slice(insertAt),
    ];
    const nextList = withSequentialIndex(updatedList);
    const unchangedOrder =
      nextList.length === orderedDataList.length &&
      nextList.every(
        (row, i) => String(row.id) === String(orderedDataList[i].id)
      );
    set({
      selectedIndexingWorkList: nextList,
      ...(!unchangedOrder ? { isChanged: true } : {}),
    });
  },
  removeSelectedIndexingWork: (id) =>
    set((s) => ({
      selectedIndexingWorkList: withSequentialIndex(
        s.selectedIndexingWorkList.filter((row) => String(row.id) !== String(id))
      ),
      isChanged: true,
    })),
  clearSelectedIndexingWorkList: () =>
    set({ isIndexingMode: false, selectedIndexingWorkList: [], isChanged: false }),
  replaceSelectedIndexingWorkList: (list) =>
    set({ selectedIndexingWorkList: withSequentialIndex(list) }),
  clearWorkIndexingChangedFlag: () => set({ isChanged: false }),
}));
